import React, { createContext, useContext, useState, useCallback } from "react";

const DICT = {
  en: {
    nav: { product: "Product", story: "Story", why: "Why Green", calculator: "Savings", stories: "Stories", contact: "Get a Quote", lang: "FR" },
    account: {
      signIn: "Sign in", signOut: "Sign out", create: "Create account", myQuotes: "My quotes",
      welcome: "Welcome back", join: "Create your account",
      name: "Full name", email: "Email address", password: "Password",
      loginCta: "Sign in", registerCta: "Create account",
      haveAccount: "Already have an account?", noAccount: "New here?",
      switchToLogin: "Sign in", switchToRegister: "Create one",
      quotesTitle: "My quote requests", quotesSub: "Everything you've requested from us, in one place.",
      quotesEmpty: "You haven't requested a quote yet.", quotesEmptyCta: "Get a quote",
      loggingIn: "Signing in…", registering: "Creating…", greeting: "Hi",
      quoteStatus: "Status", quoteDate: "Requested",
    },
    hero: {
      kicker: "Solar-backed power storage · Built for Africa",
      line1: "Africa,",
      line2: "Powered.",
      sub: "Clean, silent, always-on energy for African homes and businesses. No fumes. No outages. Just power that never sleeps.",
      cta: "Get a Quote",
      cta2: "Watch the film",
      scroll: "Scroll to explore",
      nowPlaying: "Watch the story",
    },
    marquee: ["Silent", "Solar-backed", "Always-on", "Zero emissions", "Made for Africa", "Grid-independent"],
    manifesto: {
      kicker: "The Manifesto",
      title: "Power is a right, not a privilege.",
      chapters: [
        { n: "01", t: "The blackout ends here", d: "600 million Africans live without reliable electricity. We refuse to accept the dark as normal." },
        { n: "02", t: "Sun over smoke", d: "Every Gesung unit stores clean solar energy — replacing diesel fumes with silent, breathable power." },
        { n: "03", t: "Prosperity, switched on", d: "Reliable power means open shops, studying children, and growing businesses. Energy is opportunity." },
      ],
    },
    story: {
      kicker: "The Story Arc",
      title: "One family. One spark. A movement.",
      sub: "Follow the Gesung campaign — from the frustration of the dark to a continent, powered.",
      videos: [
        { t: "The Outage", d: "A family's night interrupted — the reality millions know too well." },
        { t: "Kojo Discovers", d: "A young entrepreneur finds a better way to power his world." },
        { t: "Kojo Pitches", d: "Passing the spark forward, one neighbour at a time." },
        { t: "Testimonial", d: "Real homes. Real businesses. Real, lasting power." },
        { t: "Silent vs Diesel", d: "Same load, a world apart — watch the head-to-head." },
      ],
      soon: "Video coming soon",
    },
    versus: {
      kicker: "Hear the difference",
      title: "Silent Green vs. Diesel",
      sub: "Watch the Gesung Power Storage Generator run head-to-head against a conventional diesel generator — same load, a world apart.",
      gesung: "Gesung Green",
      diesel: "Diesel Generator",
      playCta: "Play the comparison",
      quoteCta: "Get my quote",
      soon: "Comparison video coming soon — check back shortly.",
      rows: [
        { label: "Noise level", g: "≈ 45 dB — a quiet conversation", d: "≈ 95 dB — a loud engine" },
        { label: "Emissions", g: "Zero — clean solar power", d: "High CO₂, NOx & fumes" },
        { label: "Running cost", g: "No fuel — charged by the sun", d: "Constant, rising diesel bills" },
        { label: "Maintenance", g: "Minimal — no engine to service", d: "Frequent oil & part servicing" },
        { label: "Operation", g: "Instant, automatic, 24/7", d: "Manual start, warm-up & refuelling" },
      ],
      ticker: {
        heading: "Fuel burning right now",
        diesel: "Diesel generator",
        gesung: "Gesung Green",
        burned: "burned since you opened this page",
        fuelFree: "Sun-powered · zero fuel · costs you nothing",
        yearPre: "That's about",
        yearSuf: "a year on diesel (8 h/day) — kept in your pocket.",
      },
      meter: {
        heading: "Live sound level",
        idle: "Press play to hear the difference",
        live: "Live · tap Sound to hear it",
        reacting: "Live · reacting to the video",
        quiet: "Quiet",
        loud: "Loud",
      },
      share: "Share this comparison",
      shareMsg: "Silent Green vs Diesel ⚡ The Gesung solar generator runs whisper-quiet on sunshine — no fuel, and saves about {savings} a year vs a diesel generator. See the head-to-head:",
      copyLink: "Copy link",
      linkCopied: "Link copied to clipboard",
    },
    product: {
      kicker: "The Machine",
      title: "The Gesung Power Storage Generator",
      sub: "A matte-black monolith engineered for the African climate. Solar-charged, whisper-quiet, and built to outlast the grid.",
      specs: [
        { k: "Capacity", v: "5–15 kWh", d: "Modular storage that scales with your needs" },
        { k: "Output", v: "3.6 kW", d: "Pure sine-wave, appliance-safe power" },
        { k: "Recharge", v: "Solar + Grid", d: "Tops up from panels or mains automatically" },
        { k: "Lifespan", v: "6000+ cycles", d: "LiFePO₄ cells rated for a decade of daily use" },
      ],
      features: ["Silent operation — 0 dB fumes", "App-connected monitoring", "Weather-sealed for dust & heat", "Installs in under a day"],
    },
    why: {
      kicker: "Why Green Energy for Africa",
      title: "The numbers behind the movement",
      stats: [
        { v: "600M", l: "Africans without reliable power" },
        { v: "$90B", l: "Lost annually to outages" },
        { v: "4.6M", l: "Green energy jobs by 2030" },
        { v: "70%", l: "Fuel cost cut with storage" },
      ],
    },
    compare: {
      kicker: "Green vs Brown",
      title: "A clear choice",
      green: "Gesung Green",
      brown: "Diesel / Grid",
      rows: [
        { l: "Running cost", g: "Solar-free after payback", b: "Rising fuel bills" },
        { l: "Deployment", g: "Under 1 day", b: "Weeks of works" },
        { l: "Emissions", g: "Zero on-site", b: "High CO₂ + fumes" },
        { l: "Reliability", g: "24/7 always-on", b: "Outage-prone" },
        { l: "Noise", g: "Silent", b: "Loud & constant" },
      ],
    },
    testimonials: { kicker: "Voices", title: "Powered lives" },
    roi: {
      kicker: "Savings Calculator",
      title: "See what you'd save",
      sub: "Enter your details and discover your projected savings with a Gesung system, using real regional tariffs.",
      region: "Your region",
      bill: "Average monthly power cost",
      outage: "Outage hours per week",
      resultLabel: "Estimated annual savings",
      payback: "Payback period",
      years: "years",
      note: "Estimates based on typical regional tariffs and diesel offset. Your quote will be tailored precisely.",
      cta: "Get my exact quote",
    },
    lead: {
      kicker: "Get a Quote",
      title: "Let's power your world",
      sub: "Tell us about your space. Our team will design a clean-energy solution and follow up within 24 hours.",
      savingsLabel: "You save",
      savingsSuf: "a year vs a diesel generator",
      savingsNote: "No fuel bills · no noise · clean power that pays for itself",
      name: "Full name",
      phone: "Phone number",
      email: "Email address",
      location: "Location (city / country)",
      usecase: "This is for a",
      home: "Home",
      business: "Business",
      budget: "Estimated budget / system size",
      budgetOptions: {
        unsure: "Not sure yet",
        under_2k: "Under $2,000 · small home",
        "2k_5k": "$2,000–$5,000 · home",
        "5k_15k": "$5,000–$15,000 · large home / SME",
        "15k_plus": "$15,000+ · business / estate",
      },
      message: "Anything we should know? (optional)",
      recommended: "Recommended",
      gridType: "Grid connection",
      onGrid: "On-Grid",
      offGrid: "Off-Grid",
      upfrontQ: "Pay upfront and save 5%",
      submit: "Request my quote",
      whatsapp: "Send on WhatsApp",
      submitting: "Sending...",
      success: "Request received! We'll be in touch within 24 hours.",
      error: "Something went wrong. Please try again.",
    },
    news: {
      title: "Stay in the current",
      sub: "Brand updates, launch news, and green-energy insights.",
      placeholder: "your@email.com",
      cta: "Subscribe",
      success: "You're subscribed. Welcome aboard.",
      already: "You're already subscribed.",
    },
    footer: { tagline: "Africa, Powered.", rights: "All rights reserved.", admin: "Admin", contact: "Call us" },
    whatsapp: { label: "Chat on WhatsApp", greeting: "Hello Gesung, I'd like to know more about your solar power storage solutions.", title: "Gesung Support", online: "Online now", offline: "Currently offline", hours: "Mon–Fri 8:30am–7pm · Sat 9am–2pm (GMT)", replyOnline: "We typically reply within minutes.", replyOffline: "Leave a message — we'll reply during business hours.", start: "Start chat", call: "Call us" },
    catalog: { kicker: "The Range", title: "One system for every need", sub: "From home backup to heavy industry — engineered for African conditions, priced transparently.", from: "From", cta: "Get a quote", excl: "Excludes solar PV panels (quoted separately)", priceTerms: "Prices include automatic/manual changeover switch & integrated surge protection. Solar PV panels are quoted separately based on system design. All prices are subject to prevailing market conditions at the time of final order.", datasheet: "Datasheet (PDF)", flagshipTag: "Flagship", flagshipHeadline: "The GP20K — grid independence at industrial scale", flagshipSub: "20 kW rated, 40 kW peak and 30 kWh of LiFePO₄ storage. Built to run factories, cold chains and estates straight through the longest outages.", specTitle: "Compare the range", specSub: "Every model, side by side. Real specifications straight from the 2025 catalogue.", specColSpec: "Specification", viewSpotlight: "View specifications", zoomHint: "Click to zoom", galleryPrev: "Previous", galleryNext: "Next", galleryClose: "Close", chipRated: "Rated", chipPeak: "Peak", chipStorage: "Storage", chipEff: "Efficiency", priceIn: "Show prices in", approx: "approx.", solarAdd: "Add solar PV panels", solarIncl: "incl. solar PV", autoDetected: "Auto-detected", upfrontLabel: "Pay upfront (−{pct}%)", upfrontNote: "{pct}% off applied", promoTitle: "Save {pct}% — pay cash", promoText: "Pay outright for your Gesung system and take {pct}% off. Available in your region right now.", promoApply: "Apply discount", promoApplied: "Discount applied", ghCreditTag: "Financing available", ghCredit: "Ghana credit facility: 70% upfront, remaining 30% financed over 6–24 months (by model).", warrantyGen: "15-year generator warranty", warrantyPanel: "30-year solar panel warranty", warrantyYears: "15 years", warrantyBadge: "15-yr warranty", financeLabel: "Instalments (Ghana)", financeTerm: "Term", perMonth: "/mo", monthsShort: "mo", financeDown: "70% down", financeNote: "70% upfront, balance over", interestSuffix: "interest", totalPayable: "Total payable" },
    container: {
      kicker: "Heavy Industrial",
      title: "Container-scale power, 40 kW to 200 kW",
      sub: "For mines, factories, data centres and estates — our containerised power-storage systems are custom-built to your load profile and deployed on-site.",
      cta: "Request a custom quote",
      price: "Custom quote — contact us",
      badge: "40–200 kW",
      datasheet: "Download datasheet (PDF)",
      specTitle: "Container model range",
      cols: { model: "Model", rated: "Rated", peak: "Peak", battery: "Battery", size: "Container" },
    },
    distributor: {
      kicker: "Partnership",
      title: "Become a Gesung distributor",
      sub: "We're appointing local distributors across West and East Africa. Represent Gesung in your market and grow with the clean-energy transition.",
      countriesLabel: "Now recruiting in",
      totalSuffix: "so far",
      application: "application",
      applications: "applications",
      opportunity: "The opportunity",
      mapCTA: "Apply to distribute here",
      mapHint: "Tap a country to see the opportunity",
      regionTitle: "West Africa — priority markets",
      regionSub: "Six neighbouring markets where demand for clean power is surging. Tap a country to see the opportunity and apply to distribute.",
      regionHint: "Tap a West-African market to apply",
      appointedLabel: "Appointed distributors",
      directoryTitle: "Find a distributor near you",
      directorySub: "Our appointed partners across Africa. Reach a local distributor for sales, delivery and support.",
      directorySearch: "Search by company, city or country",
      directoryAll: "All countries",
      directoryEnquire: "Enquire",
      directoryEmpty: "We're appointing distributors across Africa right now — check back soon.",
      directoryBecome: "Become a distributor",
      directoryResults: "partner",
      directoryResultsPlural: "partners",
      directoryEnquireMsg: "Hello Gesung, I'd like to connect with your distributor {company} in {city}.",
      name: "Full name",
      company: "Company name",
      phone: "Phone number",
      email: "Email address",
      country: "Country",
      countryPlaceholder: "Select your country",
      city: "City / town",
      message: "Tell us about your business (coverage, experience)",
      submit: "Apply to distribute",
      submitting: "Submitting...",
      success: "Application received! Our partnerships team will be in touch.",
      error: "Something went wrong. Please try again.",
    },
    blog: {
      kicker: "The Journal",
      title: "Green energy stories",
      sub: "Insight, economics, and impact from the frontlines of Africa's energy transition.",
      read: "Read story",
      readTime: "min read",
      all: "All stories",
      back: "Back to stories",
      empty: "No stories yet — check back soon.",
      more: "More stories",
    },
  },
  fr: {
    nav: { product: "Produit", story: "Histoire", why: "Pourquoi le Vert", calculator: "Économies", stories: "Actualités", contact: "Devis", lang: "EN" },
    account: {
      signIn: "Connexion", signOut: "Déconnexion", create: "Créer un compte", myQuotes: "Mes devis",
      welcome: "Bon retour", join: "Créez votre compte",
      name: "Nom complet", email: "Adresse e-mail", password: "Mot de passe",
      loginCta: "Se connecter", registerCta: "Créer le compte",
      haveAccount: "Vous avez déjà un compte ?", noAccount: "Nouveau ici ?",
      switchToLogin: "Se connecter", switchToRegister: "En créer un",
      quotesTitle: "Mes demandes de devis", quotesSub: "Toutes vos demandes, au même endroit.",
      quotesEmpty: "Vous n'avez pas encore demandé de devis.", quotesEmptyCta: "Obtenir un devis",
      loggingIn: "Connexion…", registering: "Création…", greeting: "Bonjour",
      quoteStatus: "Statut", quoteDate: "Demandé",
    },
    hero: {
      kicker: "Stockage d'énergie solaire · Conçu pour l'Afrique",
      line1: "L'Afrique,",
      line2: "Alimentée.",
      sub: "Une énergie propre, silencieuse et permanente pour les foyers et entreprises africains. Sans fumée. Sans coupures.",
      cta: "Obtenir un devis",
      cta2: "Voir le film",
      scroll: "Défiler pour explorer",
      nowPlaying: "Voir l'histoire",
    },
    marquee: ["Silencieux", "Solaire", "Toujours actif", "Zéro émission", "Fait pour l'Afrique", "Autonome"],
    manifesto: {
      kicker: "Le Manifeste",
      title: "L'énergie est un droit, pas un privilège.",
      chapters: [
        { n: "01", t: "Le noir s'arrête ici", d: "600 millions d'Africains vivent sans électricité fiable. Nous refusons de considérer l'obscurité comme normale." },
        { n: "02", t: "Le soleil plutôt que la fumée", d: "Chaque unité Gesung stocke une énergie solaire propre — remplaçant les fumées de diesel par une énergie silencieuse." },
        { n: "03", t: "La prospérité, allumée", d: "Une énergie fiable, ce sont des commerces ouverts, des enfants qui étudient et des entreprises qui grandissent." },
      ],
    },
    story: {
      kicker: "L'Arc Narratif",
      title: "Une famille. Une étincelle. Un mouvement.",
      sub: "Suivez la campagne Gesung — de la frustration de l'obscurité à un continent alimenté.",
      videos: [
        { t: "La Coupure", d: "La nuit d'une famille interrompue — la réalité de millions de personnes." },
        { t: "Kojo Découvre", d: "Un jeune entrepreneur trouve une meilleure façon d'alimenter son monde." },
        { t: "Kojo Convainc", d: "Transmettre l'étincelle, un voisin à la fois." },
        { t: "Témoignage", d: "De vrais foyers. De vraies entreprises. Une énergie durable." },
        { t: "Silencieux vs Diesel", d: "Même charge, un monde d'écart — regardez le face-à-face." },
      ],
      soon: "Vidéo bientôt disponible",
    },
    versus: {
      kicker: "Entendez la différence",
      title: "Silencieux Vert vs. Diesel",
      sub: "Regardez le générateur de stockage Gesung face à un générateur diesel classique — même charge, un monde d'écart.",
      gesung: "Gesung Vert",
      diesel: "Générateur Diesel",
      playCta: "Lancer la comparaison",
      quoteCta: "Obtenir mon devis",
      soon: "Vidéo comparative bientôt disponible — revenez bientôt.",
      rows: [
        { label: "Niveau sonore", g: "≈ 45 dB — une conversation calme", d: "≈ 95 dB — un moteur bruyant" },
        { label: "Émissions", g: "Zéro — énergie solaire propre", d: "CO₂, NOx et fumées élevés" },
        { label: "Coût d'exploitation", g: "Sans carburant — rechargé par le soleil", d: "Factures de diesel constantes et croissantes" },
        { label: "Entretien", g: "Minimal — aucun moteur à entretenir", d: "Vidanges et pièces fréquentes" },
        { label: "Fonctionnement", g: "Instantané, automatique, 24/7", d: "Démarrage manuel, préchauffage et ravitaillement" },
      ],
      ticker: {
        heading: "Carburant qui brûle en ce moment",
        diesel: "Générateur diesel",
        gesung: "Gesung Vert",
        burned: "brûlés depuis l'ouverture de cette page",
        fuelFree: "Alimenté par le soleil · zéro carburant · ne vous coûte rien",
        yearPre: "Soit environ",
        yearSuf: "par an en diesel (8 h/jour) — gardés dans votre poche.",
      },
      meter: {
        heading: "Niveau sonore en direct",
        idle: "Appuyez sur lecture pour entendre la différence",
        live: "En direct · activez le son",
        reacting: "En direct · réagit à la vidéo",
        quiet: "Calme",
        loud: "Fort",
      },
      share: "Partager cette comparaison",
      shareMsg: "Silencieux Vert vs Diesel ⚡ Le générateur solaire Gesung fonctionne en silence grâce au soleil — sans carburant, et économise environ {savings} par an face à un générateur diesel. Voyez le face-à-face :",
      copyLink: "Copier le lien",
      linkCopied: "Lien copié dans le presse-papiers",
    },
    product: {
      kicker: "La Machine",
      title: "Le Générateur de Stockage Gesung",
      sub: "Un monolithe noir mat conçu pour le climat africain. Rechargé au solaire, silencieux et fait pour durer.",
      specs: [
        { k: "Capacité", v: "5–15 kWh", d: "Stockage modulaire qui évolue avec vos besoins" },
        { k: "Puissance", v: "3,6 kW", d: "Onde sinusoïdale pure, sûre pour vos appareils" },
        { k: "Recharge", v: "Solaire + Réseau", d: "Se recharge automatiquement au solaire ou secteur" },
        { k: "Durée de vie", v: "6000+ cycles", d: "Cellules LiFePO₄ conçues pour une décennie" },
      ],
      features: ["Fonctionnement silencieux — 0 fumée", "Suivi connecté par application", "Étanche à la poussière et la chaleur", "Installé en moins d'une journée"],
    },
    why: {
      kicker: "Pourquoi l'Énergie Verte",
      title: "Les chiffres derrière le mouvement",
      stats: [
        { v: "600M", l: "Africains sans énergie fiable" },
        { v: "90 Md$", l: "Perdus chaque année en coupures" },
        { v: "4,6M", l: "Emplois verts d'ici 2030" },
        { v: "70%", l: "De coût de carburant en moins" },
      ],
    },
    compare: {
      kicker: "Vert contre Brun",
      title: "Un choix évident",
      green: "Gesung Vert",
      brown: "Diesel / Réseau",
      rows: [
        { l: "Coût d'exploitation", g: "Gratuit après amortissement", b: "Factures en hausse" },
        { l: "Déploiement", g: "Moins d'un jour", b: "Des semaines de travaux" },
        { l: "Émissions", g: "Zéro sur site", b: "CO₂ et fumées élevés" },
        { l: "Fiabilité", g: "24/7 permanent", b: "Sujet aux coupures" },
        { l: "Bruit", g: "Silencieux", b: "Bruyant et constant" },
      ],
    },
    testimonials: { kicker: "Voix", title: "Des vies alimentées" },
    roi: {
      kicker: "Calculateur d'Économies",
      title: "Découvrez vos économies",
      sub: "Saisissez vos informations et découvrez vos économies projetées avec un système Gesung, selon les tarifs régionaux réels.",
      region: "Votre région",
      bill: "Coût mensuel moyen d'énergie",
      outage: "Heures de coupure par semaine",
      resultLabel: "Économies annuelles estimées",
      payback: "Retour sur investissement",
      years: "ans",
      note: "Estimations basées sur les tarifs régionaux et le remplacement du diesel. Votre devis sera personnalisé.",
      cta: "Obtenir mon devis exact",
    },
    lead: {
      kicker: "Devis",
      title: "Alimentons votre monde",
      sub: "Parlez-nous de votre espace. Notre équipe concevra une solution et vous recontactera sous 24 heures.",
      savingsLabel: "Vous économisez",
      savingsSuf: "par an face à un générateur diesel",
      savingsNote: "Zéro carburant · zéro bruit · une énergie propre qui se rembourse",
      name: "Nom complet",
      phone: "Numéro de téléphone",
      email: "Adresse e-mail",
      location: "Lieu (ville / pays)",
      usecase: "C'est pour un",
      home: "Foyer",
      business: "Entreprise",
      budget: "Budget estimé / taille du système",
      budgetOptions: {
        unsure: "Je ne sais pas encore",
        under_2k: "Moins de 2 000 $ · petit foyer",
        "2k_5k": "2 000–5 000 $ · foyer",
        "5k_15k": "5 000–15 000 $ · grand foyer / PME",
        "15k_plus": "Plus de 15 000 $ · entreprise / domaine",
      },
      message: "Autre chose à savoir ? (facultatif)",
      recommended: "Recommandé",
      gridType: "Raccordement réseau",
      onGrid: "Sur réseau",
      offGrid: "Hors réseau",
      upfrontQ: "Payer d'avance et économiser 5 %",
      submit: "Demander mon devis",
      whatsapp: "Envoyer sur WhatsApp",
      submitting: "Envoi...",
      success: "Demande reçue ! Nous vous contacterons sous 24 heures.",
      error: "Une erreur s'est produite. Veuillez réessayer.",
    },
    news: {
      title: "Restez dans le courant",
      sub: "Actualités de la marque, lancements et énergie verte.",
      placeholder: "votre@email.com",
      cta: "S'abonner",
      success: "Vous êtes abonné. Bienvenue.",
      already: "Vous êtes déjà abonné.",
    },
    footer: { tagline: "L'Afrique, Alimentée.", rights: "Tous droits réservés.", admin: "Admin", contact: "Appelez-nous" },
    whatsapp: { label: "Discuter sur WhatsApp", greeting: "Bonjour Gesung, je souhaite en savoir plus sur vos solutions de stockage d'énergie solaire.", title: "Support Gesung", online: "En ligne", offline: "Actuellement hors ligne", hours: "Lun–Ven 8h30–19h · Sam 9h–14h (GMT)", replyOnline: "Nous répondons généralement en quelques minutes.", replyOffline: "Laissez un message — nous répondrons pendant les heures ouvrables.", start: "Démarrer la discussion", call: "Appelez-nous" },
    catalog: { kicker: "La Gamme", title: "Un système pour chaque besoin", sub: "Du secours domestique à l'industrie lourde — conçu pour l'Afrique, à prix transparent.", from: "À partir de", cta: "Obtenir un devis", excl: "Panneaux solaires PV non inclus (chiffrés séparément)", priceTerms: "Les prix incluent l'inverseur de source automatique/manuel et la protection contre les surtensions intégrée. Les panneaux solaires PV sont chiffrés séparément selon la conception du système. Tous les prix sont soumis aux conditions du marché en vigueur au moment de la commande finale.", datasheet: "Fiche technique (PDF)", flagshipTag: "Modèle phare", flagshipHeadline: "Le GP20K — l'indépendance énergétique à l'échelle industrielle", flagshipSub: "20 kW nominal, 40 kW en crête et 30 kWh de stockage LiFePO₄. Conçu pour faire tourner usines, chaînes du froid et domaines pendant les coupures les plus longues.", specTitle: "Comparer la gamme", specSub: "Chaque modèle, côte à côte. Spécifications réelles issues du catalogue 2025.", specColSpec: "Spécification", viewSpotlight: "Voir les spécifications", zoomHint: "Cliquer pour agrandir", galleryPrev: "Précédent", galleryNext: "Suivant", galleryClose: "Fermer", chipRated: "Nominal", chipPeak: "Crête", chipStorage: "Stockage", chipEff: "Rendement", priceIn: "Afficher les prix en", approx: "≈", solarAdd: "Ajouter des panneaux solaires PV", solarIncl: "panneaux PV inclus", autoDetected: "Détecté automatiquement", upfrontLabel: "Payer d'avance (−{pct} %)", upfrontNote: "{pct} % de remise appliquée", promoTitle: "Économisez {pct} % — payez comptant", promoText: "Payez votre système Gesung comptant et bénéficiez de {pct} % de remise. Disponible dès maintenant dans votre région.", promoApply: "Appliquer la remise", promoApplied: "Remise appliquée", ghCreditTag: "Financement disponible", ghCredit: "Facilité de crédit Ghana : 70 % à l'avance, les 30 % restants financés sur 6 à 24 mois (selon le modèle).", warrantyGen: "Garantie générateur 15 ans", warrantyPanel: "Garantie panneaux solaires 30 ans", warrantyYears: "15 ans", warrantyBadge: "Garantie 15 ans", financeLabel: "Mensualités (Ghana)", financeTerm: "Durée", perMonth: "/mois", monthsShort: "mois", financeDown: "70% d'avance", financeNote: "70% à l'avance, solde sur", interestSuffix: "intérêts", totalPayable: "Coût total" },
    container: {
      kicker: "Industrie lourde",
      title: "Puissance en conteneur, de 40 kW à 200 kW",
      sub: "Pour mines, usines, centres de données et domaines — nos systèmes de stockage conteneurisés sont conçus sur mesure selon votre profil de charge et déployés sur site.",
      cta: "Demander un devis personnalisé",
      price: "Devis personnalisé — contactez-nous",
      badge: "40–200 kW",
      datasheet: "Télécharger la fiche technique (PDF)",
      specTitle: "Gamme de modèles conteneurisés",
      cols: { model: "Modèle", rated: "Nominal", peak: "Crête", battery: "Batterie", size: "Conteneur" },
    },
    distributor: {
      kicker: "Partenariat",
      title: "Devenez distributeur Gesung",
      sub: "Nous nommons des distributeurs locaux en Afrique de l'Ouest et de l'Est. Représentez Gesung sur votre marché et grandissez avec la transition énergétique.",
      countriesLabel: "Recrutement en cours",
      totalSuffix: "à ce jour",
      application: "candidature",
      applications: "candidatures",
      opportunity: "L'opportunité",
      mapCTA: "Postuler comme distributeur ici",
      mapHint: "Touchez un pays pour voir l'opportunité",
      regionTitle: "Afrique de l'Ouest — marchés prioritaires",
      regionSub: "Six marchés voisins où la demande d'énergie propre explose. Touchez un pays pour voir l'opportunité et postuler.",
      regionHint: "Touchez un marché ouest-africain pour postuler",
      appointedLabel: "Distributeurs agréés",
      directoryTitle: "Trouvez un distributeur près de chez vous",
      directorySub: "Nos partenaires agréés à travers l'Afrique. Contactez un distributeur local pour la vente, la livraison et le support.",
      directorySearch: "Rechercher par entreprise, ville ou pays",
      directoryAll: "Tous les pays",
      directoryEnquire: "Contacter",
      directoryEmpty: "Nous nommons des distributeurs partout en Afrique — revenez bientôt.",
      directoryBecome: "Devenir distributeur",
      directoryResults: "partenaire",
      directoryResultsPlural: "partenaires",
      directoryEnquireMsg: "Bonjour Gesung, je souhaite contacter votre distributeur {company} à {city}.",
      name: "Nom complet",
      company: "Nom de l'entreprise",
      phone: "Numéro de téléphone",
      email: "Adresse e-mail",
      country: "Pays",
      countryPlaceholder: "Sélectionnez votre pays",
      city: "Ville",
      message: "Parlez-nous de votre activité (couverture, expérience)",
      submit: "Postuler comme distributeur",
      submitting: "Envoi...",
      success: "Candidature reçue ! Notre équipe partenariats vous contactera.",
      error: "Une erreur s'est produite. Veuillez réessayer.",
    },
    blog: {
      kicker: "Le Journal",
      title: "Récits d'énergie verte",
      sub: "Analyses, économie et impact au cœur de la transition énergétique de l'Afrique.",
      read: "Lire l'article",
      readTime: "min de lecture",
      all: "Tous les articles",
      back: "Retour aux articles",
      empty: "Aucun article pour l'instant — revenez bientôt.",
      more: "Plus d'articles",
    },
  },
};

const LangContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("gesung_lang") || "en");
  const toggle = useCallback(() => {
    setLang((prev) => {
      const next = prev === "en" ? "fr" : "en";
      localStorage.setItem("gesung_lang", next);
      return next;
    });
  }, []);
  return (
    <LangContext.Provider value={{ lang, setLang, toggle, t: DICT[lang] }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
